import React, { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, Loader2, Star, ChevronUp, ChevronDown, ImagePlus, ExternalLink } from "lucide-react";
import { SERVICES } from "../../data/mock";
import {
  fetchReferences, createReference, updateReference, deleteReference,
  reorderReferences, patchReference, uploadImage,
} from "../../lib/api";
import ImageUpload from "../ImageUpload";
import RefLogo from "../RefLogo";

// Turkish-aware slug helper (mirrors backend _slugify)
const _slugify = (s) =>
  (s || "")
    .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i").replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u")
    .replace(/Ç/g, "C").replace(/Ğ/g, "G").replace(/İ/g, "I").replace(/Ö/g, "O").replace(/Ş/g, "S").replace(/Ü/g, "U")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// Typing-friendly: keep trailing hyphen so separators aren't lost mid-typing
const _slugifyTyping = (s) =>
  (s || "")
    .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i").replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u")
    .replace(/Ç/g, "C").replace(/Ğ/g, "G").replace(/İ/g, "I").replace(/Ö/g, "O").replace(/Ş/g, "S").replace(/Ü/g, "U")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+/, "");

const empty = {
  id: null,
  name: "",
  slug: "",
  sector: { tr: "", en: "" },
  logo: "",
  website: "",
  service: "strategic-consulting",
  summary: { tr: "", en: "" },
  result: { tr: "", en: "" },
  metric: "",
  metric_label: { tr: "", en: "" },
  content: { tr: "", en: "" },
  testimonial: { tr: "", en: "" },
  contact_name: "",
  contact_title: { tr: "", en: "" },
  featured: false,
  order: 0,
};

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-navy-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-colors";
const labelCls = "block text-sm font-medium text-navy-900 mb-1.5";

const Bi = ({ label, path, form, set, rows, hint }) => (
  <div className="grid sm:grid-cols-2 gap-5">
    {["tr", "en"].map((l) => (
      <div key={l}>
        <label className={labelCls}>{label} ({l.toUpperCase()}){hint && <span className="text-slate-400 font-normal"> — {hint}</span>}</label>
        {rows ? (
          <textarea rows={rows} value={form[path]?.[l] || ""} onChange={(e) => set(`${path}.${l}`, e.target.value)} className={`${inputCls} resize-y`} data-testid={`ref-${path}-${l}`} />
        ) : (
          <input value={form[path]?.[l] || ""} onChange={(e) => set(`${path}.${l}`, e.target.value)} className={inputCls} data-testid={`ref-${path}-${l}`} />
        )}
      </div>
    ))}
  </div>
);

// Row-level quick logo upload (no form needed)
const QuickLogo = ({ r, onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadImage(file);
      await patchReference(r.id, { logo: url });
      onDone();
    } catch (e) {
      alert(e?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={() => ref.current?.click()}
      className="relative group shrink-0"
      title="Logo yükle"
      data-testid={`quick-logo-${r.slug}`}
    >
      <RefLogo name={r.name} logo={r.logo} />
      <span className="absolute inset-0 rounded-xl bg-navy-950/70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        {busy ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <ImagePlus className="w-4 h-4 text-white" />}
      </span>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => handle(e.target.files?.[0])} data-testid={`quick-logo-input-${r.slug}`} />
    </button>
  );
};

const ReferencesAdmin = () => {
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    fetchReferences().then(setRefs).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const set = (path, val) =>
    setForm((f) => {
      const next = { ...f };
      if (path.includes(".")) {
        const [k, sub] = path.split(".");
        next[k] = { ...(next[k] || {}), [sub]: val };
      } else next[path] = val;
      return next;
    });

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const { id, created_at, ...payload } = form;
    payload.order = Number(payload.order) || 0;
    try {
      if (id) await updateReference(id, payload);
      else await createReference(payload);
      setForm(null);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Kayıt başarısız / Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`"${r.name}" silinsin mi? / Delete this reference?`)) return;
    await deleteReference(r.id);
    load();
  };

  const move = async (idx, dir) => {
    const next = [...refs];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setRefs(next.map((r, i) => ({ ...r, order: i + 1 })));
    await reorderReferences(next.map((r) => r.id));
  };

  const toggleFeatured = async (r) => {
    setRefs((list) => list.map((x) => (x.id === r.id ? { ...x, featured: !x.featured } : x)));
    await patchReference(r.id, { featured: !r.featured });
  };

  if (form) {
    return (
      <form onSubmit={handleSave} className="bg-white rounded-2xl border border-slate-200 p-6 lg:p-8 space-y-5" data-testid="reference-form">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-xl text-navy-900">{form.id ? "Referansı Düzenle" : "Yeni Referans"}</h2>
          <button type="button" onClick={() => setForm(null)} className="text-slate-500 hover:text-navy-900 flex items-center gap-1.5"><X className="w-5 h-5" /> Kapat</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className={labelCls}>Müşteri Adı *</label>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="ref-name" />
          </div>
          <div>
            <label className={labelCls}>Web Sitesi</label>
            <input value={form.website} onChange={(e) => set("website", e.target.value)} className={inputCls} placeholder="https://" data-testid="ref-website" />
          </div>
        </div>
        <div>
          <label className={labelCls}>URL (slug)</label>
          <div className="flex items-center rounded-lg border border-slate-300 bg-white focus-within:border-amber-500 focus-within:ring-2 focus-within:ring-amber-500/20 transition-colors overflow-hidden">
            <span className="pl-4 pr-1 text-slate-400 text-sm whitespace-nowrap select-none">/referanslar/</span>
            <input
              value={form.slug || ""}
              onChange={(e) => set("slug", _slugifyTyping(e.target.value))}
              onBlur={(e) => set("slug", _slugify(e.target.value))}
              className="flex-1 bg-transparent py-2.5 pr-4 text-navy-900 outline-none"
              placeholder="ornek-musteri"
              data-testid="ref-slug"
            />
          </div>
          <p className="mt-1.5 text-xs text-slate-400">Boş bırakılırsa müşteri adından otomatik üretilir. Değiştirirseniz sayfanın adresi değişir — eski bağlantılar çalışmaz.</p>
        </div>
        <ImageUpload label="Müşteri Logosu" value={form.logo} onChange={(v) => set("logo", v)} testId="ref-logo-upload" />
        <Bi label="Sektör" path="sector" form={form} set={set} />
        <div className="grid sm:grid-cols-3 gap-5">
          <div>
            <label className={labelCls}>İlgili Hizmet</label>
            <select value={form.service} onChange={(e) => set("service", e.target.value)} className={inputCls} data-testid="ref-service">
              {SERVICES.map((s) => <option key={s.id} value={s.id}>{s.tag.tr}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Metrik (ör. %46)</label>
            <input value={form.metric} onChange={(e) => set("metric", e.target.value)} className={inputCls} data-testid="ref-metric" />
          </div>
          <div>
            <label className={labelCls}>Sıra</label>
            <input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className={inputCls} data-testid="ref-order" />
          </div>
        </div>
        <Bi label="Metrik Etiketi" path="metric_label" form={form} set={set} />
        <Bi label="Kısa Özet" path="summary" form={form} set={set} rows={3} hint="kartlarda görünür" />
        <Bi label="Sonuç" path="result" form={form} set={set} />
        <Bi label="Detaylı Hikâye" path="content" form={form} set={set} rows={8} hint="detay sayfası, paragrafları boş satırla ayırın" />
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-5 space-y-5">
          <p className="text-sm font-semibold text-navy-900">Müşteri Yorumu <span className="text-slate-400 font-normal">— kartlarda ve detay sayfasında görünür (isteğe bağlı)</span></p>
          <div>
            <label className={labelCls}>Yetkili Adı</label>
            <input value={form.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} className={inputCls} placeholder="Ad Soyad" data-testid="ref-contact-name" />
          </div>
          <Bi label="Unvan" path="contact_title" form={form} set={set} />
          <Bi label="Alıntı" path="testimonial" form={form} set={set} rows={3} />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-navy-900 cursor-pointer">
          <input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-amber-500" data-testid="ref-featured" />
          Anasayfada öne çıkar (ilk 3 gösterilir)
        </label>
        {error && <p className="text-sm text-red-500" data-testid="ref-error">{error}</p>}
        <div className="flex gap-3">
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-70 text-navy-950 font-semibold px-6 py-3 rounded-lg transition-colors" data-testid="ref-save">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Kaydet
          </button>
          <button type="button" onClick={() => setForm(null)} className="px-6 py-3 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">İptal</button>
        </div>
      </form>
    );
  }

  return (
    <div data-testid="references-admin">
      <div className="flex items-center justify-between mb-6">
        <p className="text-slate-500 text-sm">
          {refs.length} referans · <span className="text-slate-400">Logoya tıklayıp hızlı yükleyin, yıldızla öne çıkarın, oklarla sıralayın</span>
        </p>
        <button onClick={() => setForm({ ...empty, order: refs.length + 1 })} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-navy-950 font-semibold px-5 py-2.5 rounded-lg transition-colors" data-testid="new-reference-btn">
          <Plus className="w-4 h-4" /> Yeni Referans
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-20 text-slate-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
          {refs.map((r, idx) => (
            <div key={r.id} className="flex items-center gap-3 p-4" data-testid={`admin-ref-row-${r.slug}`}>
              <div className="flex flex-col shrink-0">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-0.5 text-slate-400 hover:text-navy-900 disabled:opacity-20" title="Yukarı" data-testid={`move-up-${r.slug}`}><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === refs.length - 1} className="p-0.5 text-slate-400 hover:text-navy-900 disabled:opacity-20" title="Aşağı" data-testid={`move-down-${r.slug}`}><ChevronDown className="w-4 h-4" /></button>
              </div>
              <QuickLogo r={r} onDone={load} />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-navy-900 truncate">{r.name}</h3>
                <p className="text-xs text-slate-500 mt-1 truncate">{r.sector?.tr} · {r.result?.tr}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => toggleFeatured(r)} className={`p-2 rounded-lg hover:bg-amber-50 ${r.featured ? "text-amber-500" : "text-slate-300 hover:text-amber-500"}`} title="Anasayfada öne çıkar" data-testid={`toggle-featured-${r.slug}`}>
                  <Star className={`w-4 h-4 ${r.featured ? "fill-amber-500" : ""}`} />
                </button>
                <a href={`/referanslar/${r.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg text-slate-500 hover:text-navy-900 hover:bg-slate-100" title="Sayfayı gör" data-testid={`view-ref-${r.slug}`}><ExternalLink className="w-4 h-4" /></a>
                <button onClick={() => setForm({ ...empty, ...r })} className="p-2 rounded-lg text-slate-500 hover:text-amber-600 hover:bg-amber-50" title="Düzenle" data-testid={`edit-ref-${r.slug}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(r)} className="p-2 rounded-lg text-slate-500 hover:text-red-500 hover:bg-red-50" title="Sil" data-testid={`delete-ref-${r.slug}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {refs.length === 0 && <p className="p-8 text-center text-slate-400">Henüz referans yok.</p>}
        </div>
      )}
    </div>
  );
};

export default ReferencesAdmin;
